import { fetchWithAuth } from "../auth/googleAuth";
import { pickDriveFolder, pickSpreadsheet } from "../auth/googlePicker";
import { ROOT_FOLDER_NAME } from "./drive";
import { SPREADSHEET_NAME } from "./sheets";
import { resolveAndApplyMode } from "../utils/roleResolution";

const FOLDER_MIME = "application/vnd.google-apps.folder";
const SPREADSHEET_MIME = "application/vnd.google-apps.spreadsheet";

// localStorage keys mirrored from drive.ts / sheets.ts (kept private there). Joining a
// shared project just primes these so the normal ensure* code paths resolve to the
// shared resources from cache and never hit their create/search branches.
const ROOT_ID_KEY = "mew_drive_root_id";
const SPREADSHEET_ID_KEY = "mew_spreadsheet_id";
const PICTURES_FOLDER_ID_KEY = "mew_pictures_folder_id";

export type JoinResult =
  | { status: "cancelled" }
  | { status: "owned"; name: string }
  | { status: "error"; message: string }
  | { status: "joined"; name: string };

interface FileMeta {
  id: string;
  name?: string;
  mimeType?: string;
  ownedByMe?: boolean;
  parents?: string[];
}

// Lets a fieldworker join a MewMaps project that a manager account shared with them.
//
// Under drive.file the app can only touch files it created or that the user explicitly
// selected in the Picker — and selecting a *folder* does NOT make its existing children
// listable (so we cannot discover the sheet/pictures inside a shared folder by name).
// Therefore the fieldworker picks the two resources they actually need directly:
//   1) the mew_dispensers spreadsheet (all data + config tabs), and
//   2) the pictures folder (photo uploads).
// Each Pick grants a persistent per-file grant, so this is a one-time setup: the ids are
// cached and reused on later sessions without re-picking. "Shared vs. owned" is detected
// from the spreadsheet's ownedByMe flag.
export async function joinSharedProject(): Promise<JoinResult> {
  // 1) The data spreadsheet — the essential resource.
  const pickedSheet = await pickSpreadsheet();
  if (!pickedSheet) return { status: "cancelled" };

  const metaRes = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${pickedSheet.id}?fields=id,name,mimeType,ownedByMe,parents`
  );
  if (!metaRes.ok) {
    return { status: "error", message: `Could not read the selected file (${metaRes.status}).` };
  }
  const meta = (await metaRes.json()) as FileMeta;
  if (meta.mimeType !== SPREADSHEET_MIME) {
    return { status: "error", message: `That is not a spreadsheet. Please select the shared "${SPREADSHEET_NAME}" spreadsheet.` };
  }
  if (meta.ownedByMe) {
    // The sheet belongs to the user — this is their own project, not one to join.
    return { status: "owned", name: meta.name || pickedSheet.name };
  }

  // 2) The pictures folder — needed for photo uploads. Selecting it grants write access
  // so per-dispenser subfolders can be created inside it. Required: without it, photo
  // uploads would re-search by name (which fails for a shared folder) and create a
  // duplicate "pictures" folder.
  const pickedPictures = await pickDriveFolder(`Select the shared "pictures" folder (inside "${ROOT_FOLDER_NAME}", for photos)`);
  if (!pickedPictures) {
    return { status: "error", message: "The shared \"pictures\" folder is required for photo uploads. Please select it to finish joining." };
  }
  const picMeta = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${pickedPictures.id}?fields=id,name,mimeType,parents`
  );
  if (!picMeta.ok) {
    return { status: "error", message: `Could not read the selected folder (${picMeta.status}).` };
  }
  const picData = (await picMeta.json()) as FileMeta;
  if (picData.mimeType !== FOLDER_MIME) {
    return { status: "error", message: "That selection is not a folder. Please select the project's \"pictures\" folder." };
  }
  // Must be the folder actually named "pictures" — not "assets"/"maps"/etc., which are easy
  // to grab by mistake and would silently send photo uploads to the wrong place.
  if ((picData.name || "").trim().toLowerCase() !== "pictures") {
    return { status: "error", message: `Please select the folder named "pictures" (you chose "${picData.name}").` };
  }
  // The folder must live directly inside the same "MewMaps" root as the data sheet.
  // The sheet and the pictures folder share that root as their first parent, so comparing
  // parent ids rejects any "pictures" folder picked from outside this project — without
  // needing a grant on the root itself (which drive.file doesn't give for a picked child).
  if (!meta.parents?.[0] || picData.parents?.[0] !== meta.parents[0]) {
    return { status: "error", message: `That folder is not inside this project's "${ROOT_FOLDER_NAME}" folder. Please select the "pictures" folder inside it.` };
  }

  // Commit. Root id is recorded for reference only (the sheet's parent); nothing in
  // Fieldworker mode needs write access to the root itself.
  if (meta.parents && meta.parents[0]) localStorage.setItem(ROOT_ID_KEY, meta.parents[0]);
  localStorage.setItem(SPREADSHEET_ID_KEY, pickedSheet.id);
  localStorage.setItem(PICTURES_FOLDER_ID_KEY, pickedPictures.id);
  // Non-owner: resolve manager vs. fieldworker from the project's Members tab (a non-owner
  // listed as "manager" gets the full UI; everyone else is a fieldworker).
  await resolveAndApplyMode({ spreadsheetId: pickedSheet.id, ownedByMe: false });

  return { status: "joined", name: meta.name || pickedSheet.name };
}
